/**
 * SYS-B-05 (Phase 4 C6) — Contrôle des soldes avant clôture de période.
 */
import type { Ecriture, Balance } from '@shared/types/Ecriture';
import { BalanceService } from './BalanceService';
import { roundFCFA } from '@shared/utils/round';

export class BalancePeriodeDesequilibreeError extends Error {
  constructor(
    public readonly periode: string,
    public readonly totalDebit: number,
    public readonly totalCredit: number,
  ) {
    const ecart = roundFCFA(totalDebit - totalCredit);
    super(
      `Clôture refusée : balance de ${periode} déséquilibrée (débit ${totalDebit} ≠ crédit ${totalCredit}, écart ${ecart} FCFA).`,
    );
    this.name = 'BalancePeriodeDesequilibreeError';
  }
}

export type ControleSoldesResultat = {
  periode: string;
  balance: Balance;
  totalDebit: number;
  totalCredit: number;
  equilibre: boolean;
  ecart: number;
};

export function controlerSoldesPeriode(
  ecritures: readonly Ecriture[],
  periode: string,
  tolerance = 0,
): ControleSoldesResultat {
  const duMois = ecritures.filter((e) => e.periode === periode);
  const balance = BalanceService.calculer(duMois);
  let totalDebit = 0;
  let totalCredit = 0;
  for (const ligne of Object.values(balance)) {
    totalDebit += ligne.debit;
    totalCredit += ligne.credit;
  }
  totalDebit = roundFCFA(totalDebit);
  totalCredit = roundFCFA(totalCredit);
  const ecart = roundFCFA(totalDebit - totalCredit);
  return {
    periode,
    balance,
    totalDebit,
    totalCredit,
    equilibre: Math.abs(ecart) <= tolerance,
    ecart,
  };
}

export function assertSoldesEquilibresAvantCloture(
  ecritures: readonly Ecriture[],
  periode: string,
  tolerance = 0,
): ControleSoldesResultat {
  const r = controlerSoldesPeriode(ecritures, periode, tolerance);
  if (!r.equilibre) {
    throw new BalancePeriodeDesequilibreeError(periode, r.totalDebit, r.totalCredit);
  }
  return r;
}
